import { AMPLITUDE_ACTIONS } from '@/modules/core/amplitude/events';
import { LogEvent } from '@/modules/core/amplitude/types/Amplitude';
import { GenericObject } from '@/modules/core/data/types/GenericObject';
import { getStepFullLabel } from '@/modules/solutions/educationalProgramStep';
import { filterUnusedContextEducationalPrograms } from '@/modules/solutions/studentSolutionContext';
import { Amplitude } from '@amplitude/react-amplitude';
import {
  css,
  useTheme,
} from '@emotion/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classnames from 'classnames';
import map from 'lodash.map';
import size from 'lodash.size';
import sortBy from 'lodash.sortby';
import React from 'react';
import {
  Trans,
  useTranslation,
} from 'react-i18next';
import {
  Col,
  Row,
} from 'reactstrap';
import { ContextFiltersSettings } from '@/modules/core/data/types/ContextFiltersSettings';
import { Campus } from '@/modules/core/data/types/Campus';
import { CustomerTheme } from '@/modules/core/data/types/CustomerTheme';
import { EducationalProgramStep } from '@/modules/core/data/types/EducationalProgramStep';
import { StudentSolutionsCategory } from '@/modules/core/data/types/StudentSolutionsCategory';
import { SetIsContextBuilderOpen } from '@/modules/core/data/types/SetIsContextBuilderOpen';
import { StudentSolutionContext } from '@/modules/core/data/types/StudentSolutionContext';
import Btn from '@/components/dataDisplay/Btn';
import CircleBtn from '@/components/dataDisplay/CircleBtn';

type Props = {
  contextFiltersSettings: ContextFiltersSettings;
  contextFilters: StudentSolutionContext;
  setIsContextBuilderOpen: SetIsContextBuilderOpen;
}

/**
 * Renders the builder when it's closed.
 *
 * @return {JSX.Element}
 * @constructor
 */
const ClosedBuilder: React.FunctionComponent<Props> = (props): JSX.Element => {
  const {
    contextFiltersSettings,
    contextFilters,
    setIsContextBuilderOpen,
  } = props;
  const {
    educationalPrograms: contextEducationalPrograms, // eslint-disable-line no-unused-vars, @typescript-eslint/no-unused-vars
    educationalProgramSteps: contextEducationalProgramSteps,
    campuses: contextCampuses,
    categories: contextCategories,
  } = contextFilters;
  const { t } = useTranslation();
  const theme: CustomerTheme = useTheme();
  const { primaryColor } = theme;
  const sanitizedContextFilters: StudentSolutionContext = filterUnusedContextEducationalPrograms(contextFilters);
  const filterColumnSize: number = Math.floor(12 / contextFiltersSettings.enabledFiltersCount);
  const classNameFilterColumnSize = `lg-${filterColumnSize}`;

  return (
    <Amplitude
      eventProperties={(inheritedProps): GenericObject => ({
        ...inheritedProps,
      })}
    >
      {({ logEvent }: { logEvent: LogEvent }): JSX.Element => (
        <Row
          css={css`
            min-height: 50px;
            max-height: 150px;
            overflow-y: scroll;

            .content {
              p {
                margin-bottom: 0;
              }
            }

            .has-more-options-mask {
              pointer-events: none;
              position: absolute;
              bottom: 0;
              z-index: 1;
              background: linear-gradient(0deg, ${primaryColor}, transparent);
              height: 100px;

              // Take full width, but reset horizontal margins created by parent Row to avoid increasing page's width
              width: 100vw;
              margin-left: -15px;
              margin-right: -15px;
            }

            @media (max-width: 991.98px) {
              margin-top: 35px;

              .content {
                font-size: 12px;
              }
            }
          `}
        >
          <div
            className={classnames(size(contextEducationalProgramSteps) > 5 ? 'has-more-options-mask' : '')}
          >
          </div>
          {
            contextFiltersSettings.campusFilterEnabled && (
              <Col
                className={classnames(classNameFilterColumnSize)}
              >
                <div className={classnames('title')}>
                  {t('solutionsPage.contextBuilder.campusFilter.title', 'Campus')}
                  <CircleBtn>
                    {size(sanitizedContextFilters.campuses)}
                  </CircleBtn>
                </div>
                <div className={classnames('content')}>
                  {
                    !!size(contextCampuses) ?
                      map(sortBy(contextCampuses, 'label'), (campus: Campus) => {
                        return (
                          <div
                            key={campus?.id}
                            className={'center'}
                          >
                            {campus?.label}
                          </div>
                        );
                      }) : (
                        <div
                          className={'center'}
                        >
                          <Trans i18nKey="solutionsPage.closedBuilder.noCampusFilter">
                            Aucun filtre par <strong>campus</strong> n'est appliqué
                          </Trans>
                        </div>
                      )
                  }
                </div>
              </Col>
            )
          }

          {
            contextFiltersSettings.educationalProgramFilterEnabled && (
              <Col
                className={classnames(classNameFilterColumnSize)}
              >
                <div className={classnames('title')}>
                  {t('solutionsPage.contextBuilder.programFilter.title', 'Programmes')}
                  <CircleBtn>
                    {size(sanitizedContextFilters.educationalPrograms)}
                  </CircleBtn>
                </div>
                <div className={classnames('content')}>
                  {
                    !!size(contextEducationalProgramSteps) ?
                      map(sortBy(contextEducationalProgramSteps, 'label'), (step: EducationalProgramStep) => {
                        return (
                          <div
                            key={step?.id}
                            className={'center'}
                          >
                            {getStepFullLabel(step)}
                          </div>
                        );
                      }) : (
                        <div
                          className={'center'}
                        >
                          <Trans i18nKey="solutionsPage.closedBuilder.noProgramFilter">
                            Aucun filtre par <strong>programme</strong> n'est appliqué
                          </Trans>
                        </div>
                      )
                  }
                </div>
              </Col>
            )
          }

          {
            contextFiltersSettings.categoryFilterEnabled && (
              <Col
                className={classnames(classNameFilterColumnSize)}
              >
                <div className={classnames('title')}>
                  {t('solutionsPage.contextBuilder.categoryFilter.title', 'Catégories')}
                  <CircleBtn>
                    {size(contextFilters.categories)}
                  </CircleBtn>
                </div>
                <div className={classnames('content')}>
                  {
                    !!size(contextCategories) ? map(contextCategories, (tag: StudentSolutionsCategory) => {
                      return (
                        <div
                          key={tag?.id}
                          className={'center'}
                        >
                          {tag?.label}
                        </div>
                      );
                    }) : (
                      <div
                        className={'center'}
                      >
                        <Trans i18nKey="solutionsPage.closedBuilder.noTagFilter">
                          Aucun filtre par <strong>tag</strong> n'est appliqué
                        </Trans>
                      </div>
                    )
                  }
                </div>
              </Col>
            )
          }

          <Btn
            className={classnames('toggle-button')}
            mode={'primary-reverse'}
            onClick={(): void => {
              setIsContextBuilderOpen(true);

              logEvent('solutions-contextBuilderBtn-clicked', {
                action: AMPLITUDE_ACTIONS.OPEN,
              });
            }}
          >
            <FontAwesomeIcon
              icon="pen"
            />
            <span className={'hide-sm-md'}>
              {t('solutionsPage.closedBuilder.openBtn', 'Modifier')}
            </span>
          </Btn>
        </Row>
      )}
    </Amplitude>
  );
};

export default ClosedBuilder;
